// Centro de Llamadas (Etapa 2, SIMULADA) -- utilidades compartidas del front.
// El backend es la autoridad de permisos; aquí solo se formatean mensajes y etiquetas.
import api, { formatApiError } from "./api";

export const d = {
  get: (path, config) => api.get(`/dialer${path}`, config),
  post: (path, body, config) => api.post(`/dialer${path}`, body ?? {}, config),
  put: (path, body, config) => api.put(`/dialer${path}`, body ?? {}, config),
  delete: (path, config) => api.delete(`/dialer${path}`, config),
};

// Mensaje legible para el usuario a partir de un error de axios.
export function dialerError(e) {
  const detail = e?.response?.data?.detail;
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    if (detail.msg) return detail.msg;
    if (detail.code === "revision_requerida") return "La lista tiene observaciones: revisa el reporte antes de guardar.";
    if (detail.code === "puesto_con_otra_campana_en_marcha") return "Este puesto ya tiene otra campaña en marcha; páusala primero.";
    if (detail.code === "puesto_ocupado") return "Ya hay una llamada viva en este puesto.";
    if (detail.code === "ambiguo") return "Varias fichas coinciden con este número; elige una.";
    if (detail.code === "confirmacion_requerida") return "Caso dudoso: confirma si es persona o buzón antes de cerrar.";
    if (detail.code === "llamada_viva") return "Hay una llamada viva en el puesto; ciérrala primero.";
  }
  if (e?.response?.status === 403) return "No tienes permiso para esta acción.";
  if (e?.response?.status === 404 && typeof detail === "string") return detail;
  return formatApiError(detail ?? e?.message);
}

export const RESULT_LABELS = {
  contestada: "Contestada",
  rechazada: "Rechazada",
  buzon_confirmado: "Buzón",
  caso_dudoso: "Caso dudoso",
  sin_respuesta: "Sin respuesta",
};

// Resultados que pone el SISTEMA (no los elige una persona): llamadas que se detuvieron antes de marcar al cliente.
export const SYSTEM_RESULT_LABELS = {
  ruta_no_disponible: "La ruta no está disponible: no se marcó al cliente",
  central_no_responde: "La central no respondió: no se marcó al cliente",
  telefono_del_puesto_no_contesto: "El teléfono del puesto no contestó: no se marcó al cliente",
  no_se_pudo_marcar: "No se pudo marcar: el puesto no tiene teléfono o la ruta no sirve",
  resultado_desconocido_revisar: "Estado por confirmar: decide qué pasó con esta llamada",
};
// Cierres rápidos de una llamada del dialer "por fuera" (no se ofrecen en los selectores de resultado: viven aparte de RESULT_LABELS).
export const EXTERNAL_RESULT_LABELS = { no_dijo_nada: "No dijo nada", llamada_mala: "Llamada mala", abusivo: "Abusivo", otra: "Otra" };
export const resultLabel = (r) => RESULT_LABELS[r] || EXTERNAL_RESULT_LABELS[r] || SYSTEM_RESULT_LABELS[r] || r;

// H22: cierre inteligente de buzón / mensaje de operadora (textos mínimos; la pantalla es del turno 4).
export const VOICEMAIL_MODE_LABELS = {
  automatico: "Automático: cuelga solo si está muy seguro",
  sugerir: "Solo sugerir",
  apagado: "Apagado",
};
export const AUTO_CLOSE_KIND_LABELS = { buzon: "Buzón de voz", operadora: "Mensaje de operadora" };
export const AUTO_CLOSE_LAYER_LABELS = { reglas: "Reglas rápidas", ia: "IA" };
export const autoCloseText = (ac) =>
  ac ? `Cerrada por el sistema: ${AUTO_CLOSE_KIND_LABELS[ac.kind] || ac.kind} (${Math.round((ac.confidence || 0) * 100)}%)` : "";

// Pantalla del cierre automático (Turno 4): etiqueta corta "🤖 Cerrada sola: ..." con la hora y una línea del motivo.
export const AUTO_CLOSE_SHORT_LABEL = { buzon: "buzón", operadora: "mensaje de operadora" };
export function autoCloseTag(ac) {
  if (!ac) return "";
  const when = fmtTime(ac.at || ac.closed_at);
  return `🤖 Cerrada sola: ${AUTO_CLOSE_SHORT_LABEL[ac.kind] || ac.kind || "buzón"}${when ? ` · ${when}` : ""}`;
}
export const autoCloseReason = (ac) => (ac?.reason || ac?.phrase || "").trim();

export const CAMPAIGN_STATUS = {
  borrador: { label: "Borrador", cls: "bg-zinc-500/20 text-zinc-300 border-zinc-500/30" },
  lista_para_iniciar: { label: "Lista para iniciar", cls: "bg-sky-500/20 text-sky-300 border-sky-500/30" },
  en_marcha: { label: "En marcha", cls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" },
  pausada: { label: "Pausada", cls: "bg-amber-500/20 text-amber-300 border-amber-500/30" },
  detenida: { label: "Detenida", cls: "bg-red-500/20 text-red-300 border-red-500/30" },
  finalizada: { label: "Finalizada", cls: "bg-zinc-500/20 text-zinc-300 border-zinc-500/30" },
};

export const PAUSE_REASONS = {
  // H26 (Turno 4c, orden del Founder): la franja de estado ya no repite "pausada" dos veces ("PAUSADA — Pausada
  // por el operador"); el título pasó a "EN PAUSA" (ver CAMPAIGN_BANNER) y este motivo cuenta qué hacer, no repite.
  operador: "Tú la pausaste. Pulsa Continuar para seguir.",
  llamada_manual: "Pausada por una llamada manual",
  // H26-sexies (Ticket 4c-quinquies, corrección: Founder vio en Chrome que tras colgar una llamada normal
  // ["persona"/finish_call] la campaña queda pausada con pause_reason=conexion_activa pero NO sigue sola --
  // solo el cierre automático de buzón la reanuda sola, ver backend/routes/dialer_center.py ~línea 1346). La
  // frase estática anterior ("Sigue sola cuando termine") era falsa casi siempre: "conexion_activa" ahora es un
  // caso ESPECIAL en pauseReasonText(reason, readOnly, callLive), nunca una entrada fija de este diccionario.
  reinicio: "Pausada tras reiniciar el sistema",
  falla_origen: "Pausada: falló al originar una llamada",
  cobertura_revocada: "Pausada: se revocó la cobertura durante una llamada",
  ruta_no_disponible: "Pausada: la ruta no está disponible (revisa la ruta antes de continuar)",
  central_no_responde: "Pausada: la central no está respondiendo (avisa al administrador; reanuda cuando esté normal)",
  telefono_del_puesto: "Pausada: el teléfono del puesto no contestó (revisa que esté conectado)",
};

// H26-bis (Turno 4c-bis, orden del Founder tras ver la cinta en Chrome): quien pausa "a mano" en el puesto es
// SIEMPRE el asistente (dueño del puesto); el bilingüe solo mira (readOnly). "Tú la pausaste" es falso para él
// -- estas son las frases que SÍ aplican cuando lo lee el bilingüe, en solo lectura.
export const PAUSE_REASONS_READONLY = {
  operador: "Tu asistente pausó. Sigue cuando él continúe.",
};

export function fmtPhone(p) {
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(p || "");
  return m ? `+1 (${m[1]}) ${m[2]}-${m[3]}` : p || "";
}

// El backend serializa fechas de Mongo en UTC SIN sufijo de zona: sin "Z" el navegador las leería como hora local.
export function parseUtc(iso) {
  if (!iso) return null;
  const s = String(iso);
  const hasZone = /(Z|[+-]\d{2}:?\d{2})$/.test(s);
  const dt = new Date(hasZone ? s : `${s}Z`);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

export function fmtTime(iso) {
  if (!iso) return "";
  const dt = parseUtc(iso);
  if (!dt) return "";
  return dt.toLocaleTimeString("es-DO", { timeZone: "America/Santo_Domingo", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
}

export function fmtDateTime(iso) {
  if (!iso) return "";
  const dt = parseUtc(iso);
  if (!dt) return "";
  return dt.toLocaleString("es-DO", { timeZone: "America/Santo_Domingo", dateStyle: "short", timeStyle: "short" });
}

// H5 (Turno 4, corrección 2026-10-22): "Cliente de la llamada" arma un objeto `obs`/`call` RECORTADO para pasarlo a
// <LlamadaSinFicha>/<ClienteDeLaLlamadaCard>. Antes se le olvidaba `connected_at`/`started_at`, así que el cronómetro
// "EN LÍNEA" se quedaba en 00:00 aunque `green` fuera true (venía de OTRO objeto, el `call` real con esas fechas).
// Estas dos funciones puras son el ÚNICO lugar donde se arman esos objetos recortados (AsistentePanel.jsx,
// BilingueCobertura.jsx, BilinguePanel.jsx), para no volver a duplicar el bug en un cuarto sitio.
export function buildObsForCard(call, extra) {
  if (!call) return { mode: "burbuja", connected: false };
  return {
    mode: "burbuja", call_id: call.id, client_id: call.client_id, client_name: call.client_name, phone: call.phone,
    client_version: call.client_version, connected_at: call.connected_at, started_at: call.started_at, external: !!call.external, ...extra,
  };
}
export function callFromObs(obs) {
  if (!obs?.call_id) return null;
  return {
    id: obs.call_id, client_id: obs.client_id, client_name: obs.client_name, phone: obs.phone,
    client_version: obs.client_version, connected_at: obs.connected_at, started_at: obs.started_at, state: obs.state, external: !!obs.external,
  };
}

export const LIVE_CALL_STATES = ["reservada", "marcando", "conectada"];
export const isLive = (call) => !!call && LIVE_CALL_STATES.includes(call.state);

export function callStateLabel(call) {
  if (!call) return "Libre";
  switch (call.state) {
    case "reservada": return "Preparando…";
    case "marcando": return "Marcando…";
    case "conectada": return "En llamada";
    case "terminada": return "Finalizada";
    case "desconocida": return "Resultado desconocido";
    case "descartada": return "Descartada";
    default: return call.state;
  }
}

// ---- Ampliación V4 (SIMULADA) ----
export const TRANSFER_LABELS = {
  consultando: "Consultando al destino (cliente en espera)",
  destino_confirmado: "El destino aceptó: listo para entregar",
  destino_no_disponible: "El destino no está disponible (ocupado/rechazó/cayó)",
  entregando: "Orden enviada: falta el resultado confirmado",
  entrega_rechazada: "La entrega fue rechazada: la llamada sigue contigo",
  incierto: "Resultado incierto: decide qué pasó",
  cancelada: "Transferencia cancelada",
  completada: "Transferencia completada",
};

export const LOOKUP_KIND_LABELS = { telefono_inverso: "Teléfono inverso", direccion: "Dirección" };

export const LOOKUP_STATUS = {
  reservada: { label: "Reservada", cls: "border-zinc-500/40 text-zinc-300" },
  enviando: { label: "Consultando…", cls: "border-amber-500/40 text-amber-300" },
  con_resultado: { label: "Con resultado", cls: "border-emerald-500/40 text-emerald-300" },
  sin_coincidencias: { label: "Sin coincidencias", cls: "border-zinc-500/40 text-zinc-300" },
  error_confirmado: { label: "Error confirmado", cls: "border-red-500/40 text-red-300" },
  resultado_incierto: { label: "Resultado incierto", cls: "border-orange-500/50 text-orange-300" },
};

// Avisa a la burbuja (otra parte del árbol) que algo cambió en el backend: relee de inmediato.
export const notifyDialerChanged = () => { try { window.dispatchEvent(new Event("dialer:changed")); } catch (e) { /* sin window */ } };

// Clave de operación estable por llamada (sobrevive a recargar: un reintento NO crea otra ficha).
export function operationKeyFor(callId) {
  const k = `dialer.opkey.${callId}`;
  try {
    let v = window.sessionStorage.getItem(k);
    if (!v) { v = (window.crypto?.randomUUID?.() || `op-${Date.now()}-${Math.random().toString(16).slice(2)}`); window.sessionStorage.setItem(k, v); }
    return v;
  } catch (e) { return `op-${Date.now()}-${Math.random().toString(16).slice(2)}`; }
}
export function clearOperationKey(callId) { try { window.sessionStorage.removeItem(`dialer.opkey.${callId}`); } catch (e) { /* nada */ } }

// H16/H25 (corrección 2026-10-22, verificación en Chrome): en la "Cola de números", cuál fila es "MARCANDO AHORA"
// y cuál es la que "SIGUE" (la próxima). El backend (ver _cola_window en dialer_center.py) ya manda `queue_role`
// ('ahora'|'sigue'|'reciente') calculado con la MISMA consulta/orden que usa el marcador real -- por eso el
// frontend NUNCA debe adivinarlo por `status`/`position`. Se deja un respaldo por status solo para listas
// viejas que no traigan `queue_role` (p. ej. pruebas existentes que arman números a mano).
export function queueCursor(numbers) {
  const list = numbers || [];
  if (list.some((n) => n?.queue_role)) {
    return { marking: list.findIndex((n) => n.queue_role === "ahora"), next: list.findIndex((n) => n.queue_role === "sigue") };
  }
  const marking = list.findIndex((n) => n.status === "en_curso" || n.status === "desconocido_revisar");
  const nextIdx = list.findIndex((n, i) => n.status === "pendiente" && i !== marking && (marking < 0 || i > marking));
  return { marking, next: nextIdx };
}

// H25/H26-bis/H26-sexies: por qué está pausada una campaña, en palabras simples (para la franja de estado).
// `readOnly` cambia los motivos donde importa quién pausó ("operador"); `callLive` es SOLO para "conexion_activa"
// (el único motivo que a veces sigue sola y a veces no -- ver nota en PAUSE_REASONS): con la llamada todavía
// viva no se promete nada ("Hay un cliente en la línea."); ya colgada, exige Continuar explícito (salvo que el
// sistema ya la haya reanudado solo por cierre de buzón, caso en el que el status ya no sería "pausada").
export function pauseReasonText(reason, readOnly = false, callLive = false) {
  if (reason === "conexion_activa") {
    if (callLive) return "Hay un cliente en la línea.";
    return readOnly ? PAUSE_REASONS_READONLY.operador : "La llamada terminó. Pulsa Continuar para seguir.";
  }
  return (readOnly && PAUSE_REASONS_READONLY[reason]) || PAUSE_REASONS[reason] || "Pausada";
}

// H25: mapeo PURO status -> insignia de la franja/botón principal (sin JSX, para poder probarlo sin montar el DOM:
// este proyecto no tiene @testing-library/react instalado). <CampaignStatusBanner> y los botones de Iniciar/
// Continuar/Pausar/Detener se apoyan en esta misma tabla para no decidir el estado en dos sitios distintos.
export const CAMPAIGN_BANNER = {
  en_marcha: { cls: "on", title: "CAMPAÑA ENCENDIDA", primaryButton: "en_marcha_inerte" },
  // H26 (Turno 4c, maqueta cinta_campana.html aprobada): "PAUSADA" -> "EN PAUSA" para que la frase de abajo
  // (p.ej. "Pausada por el operador") nunca repita la misma palabra que el título.
  pausada: { cls: "pause", title: "EN PAUSA", primaryButton: "continuar" },
  detenida: { cls: "off", title: "DETENIDA", primaryButton: "ninguno" },
  lista_para_iniciar: { cls: "idle", title: "EN ESPERA", primaryButton: "iniciar" },
};
export function campaignBannerInfo(status) {
  return CAMPAIGN_BANNER[status] || null;
}

// H26 (Turno 4c): la frase de apoyo (bajo el título) de la franja de la campaña -- UNA sola, nunca repite el
// título. Antes vivía inline en <CampaignStatusBanner>; se saca aquí para poder probarla sin montar el DOM
// (este proyecto no tiene @testing-library/react), igual que ya se hizo con campaignBannerInfo.
export function campaignBannerSubtitle(status, { readOnly = false, markingLabel, pauseReason, callLive = false } = {}) {
  if (status === "en_marcha") return markingLabel || (readOnly ? "Tu asistente tiene la campaña marcando" : "Marcando…");
  if (status === "pausada") return pauseReasonText(pauseReason, readOnly, callLive);
  if (status === "detenida") return "Esta campaña está cerrada; no vuelve a marcar.";
  if (status === "lista_para_iniciar") return readOnly ? "Tu asistente aún no la ha iniciado." : "No marca hasta que pulses Iniciar.";
  return "";
}

// H26 (Turno 4c, maqueta cinta_campana.html aprobada): color/etiqueta/rol de UNA fila de "la cinta" de la
// campaña. PURA (nada de JSX/fetch) para poder probarla sin DOM. Usa `queue_role` del backend igual que
// queueCursor -- NUNCA adivina "ahora"/"sigue" por status/position (ese fue el bug de H16).
// Colores de marca: rojo=llamando o sin contestar, verde=contestó, azul=cerrada sola (buzón), ámbar=reintento
// o mensaje de operadora, gris=sigue/en cola.
export const ROW_COLOR_VAR = { red: "var(--cc-red)", green: "var(--cc-ok)", blue: "var(--cc-call)", amber: "var(--cc-amber)", gray: "var(--cc-mut)" };
export function numberRowVisual(n, call) {
  const role = n?.queue_role || "reciente";
  const ac = n?.last_auto_close;
  if (ac) {
    const buzon = (ac.kind || "buzon") !== "operadora";
    return { color: buzon ? "blue" : "amber", role, label: buzon ? "BUZÓN" : "OPERADORA" };
  }
  if (role === "ahora") {
    return call?.state === "conectada"
      ? { color: "green", role, label: "CONTESTÓ" }
      : { color: "red", role, label: "LLAMANDO" };
  }
  if (n?.status === "terminado") {
    if (n.final_result === "contestada") return { color: "green", role, label: "CONTESTÓ" };
    if (n.final_result === "sin_respuesta") return { color: "red", role, label: "SIN RESP." };
    return { color: "gray", role, label: (RESULT_LABELS[n.final_result] || "LISTO").toUpperCase() };
  }
  if (role === "sigue") return { color: "gray", role, label: "SIGUE" };
  if ((n?.attempts || 0) > 0) {
    const hm = n?.next_attempt_at ? fmtTime(n.next_attempt_at).slice(0, 5) : "";
    return { color: "amber", role, label: hm ? `REINTENTO ${hm}` : "REINTENTO" };
  }
  return { color: "gray", role, label: "EN COLA" };
}

// H26-bis (Turno 4c-bis, maqueta cinta_campana.html, reloj de la fila "ahora"): PURAS, un solo temporizador
// compartido en el componente (setInterval de 1 s que recalcula el texto), nunca un intervalo por fila.
// 25 s = DIALER_CUSTOMER_RING_SECONDS en backend/routes/dialer_center.py (default_customer_ring_seconds); no hay
// endpoint que la entregue al frontend, así que se refleja aquí el mismo valor por defecto (no se tocó backend).
export const DIALER_CUSTOMER_RING_SECONDS = 25;

export function fmtMMSS(totalSeconds) {
  if (!totalSeconds && totalSeconds !== 0) return "00:00";
  const s = Math.max(0, Math.round(totalSeconds));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

function secondsSince(iso, nowMs) {
  const start = parseUtc(iso)?.getTime();
  if (!start) return null;
  return Math.max(0, Math.floor((nowMs - start) / 1000));
}

// "00:11 / 25 s" mientras timbra (desde que se originó la llamada / se reservó el número).
export function ringClockText(startIso, nowMs = Date.now()) {
  const secs = secondsSince(startIso, nowMs);
  return secs === null ? null : `${fmtMMSS(secs)} / ${DIALER_CUSTOMER_RING_SECONDS} s`;
}

// "EN LÍNEA 01:32" mientras hay conversación conectada.
export function connectedClockText(connectedAtIso, nowMs = Date.now()) {
  const secs = secondsSince(connectedAtIso, nowMs);
  return secs === null ? null : `EN LÍNEA ${fmtMMSS(secs)}`;
}

// Fila "ahora" del ASISTENTE: trae la llamada real (state/started_at/connected_at), igual que buildObsForCard.
export function rowClockText(call, nowMs = Date.now()) {
  if (!call) return null;
  if (call.state === "conectada" && call.connected_at) return connectedClockText(call.connected_at, nowMs);
  if (call.started_at && (call.state === "marcando" || call.state === "reservada")) return ringClockText(call.started_at, nowMs);
  return null;
}

// Fila "ahora" de la COLA DEL BILINGÜE: el resumen (GET /observer/summary) no trae el estado exacto de la
// llamada viva (ver nota en BilingueExtras.ColaTab), solo el número con `claimed_at` (cuándo se tomó para marcar);
// por eso siempre se lee como timbrando, nunca "EN LÍNEA" -- igual que ya decidía numberRowVisual para ese rol.
export function colaRowClockText(n, nowMs = Date.now()) {
  if (!n || n.queue_role !== "ahora" || !n.claimed_at) return null;
  return ringClockText(n.claimed_at, nowMs);
}

// H10: resultado al colgar SIN preguntar. Solo si la llamada nunca conectó (cancelada mientras timbraba) es "sin_respuesta".
// Si conectó devuelve null: hay que preguntar "¿Cómo terminó?" y usar la respuesta de la persona.
export const hangupDirectResult = (connected) => (connected ? null : "sin_respuesta");

// Ticket 4b-4 (orden del Founder: "que no me pregunte nada al cerrar"): qué hacer al pulsar "Colgar y
// cerrar"/"Cancelar" -- PURA, para poder probarla sin montar el teléfono. Nunca hay una pantalla "¿Cómo
// terminó?": "direct" y "manual" cierran con un resultado ya decidido (/finish); "pending" cuelga YA y deja la
// llamada "por revisar" en la cinta, sin bloquear nada (/hangup-pending).
export function hangupAction(call, connected) {
  const direct = hangupDirectResult(connected);
  if (direct) return { kind: "direct", result: direct };
  if (call?.mode === "manual") return { kind: "manual", result: "contestada" };
  return { kind: "pending", result: null };
}

// Ticket 4b-4-bis (bug grave: "la central ya colgó pero la pantalla sigue EN LLAMADA" -- sesión SIP.js local
// "zombie", nunca procesó el BYE). PURA: decide si hay que terminar la sesión SIP local YA, sin esperar más al
// servidor. Solo dispara con un estado CONFIRMADO de "ya terminó" (nunca con `callState` vacío/desconocido:
// un call recién marcado, antes de que llegue el primer GET /calls/{id}, NO debe cortarse por error).
const HANGUP_RECONCILE_STATES = ["terminada", "descartada"];
export function shouldForceLocalHangup({ inCall, callState }) {
  return !!inCall && HANGUP_RECONCILE_STATES.includes(callState);
}

// H1 (Codex): el sondeo limpia `call` en cuanto el servidor la da por terminada, así que el efecto de reconciliación ya no ve el estado
// terminal. Esto se llama con la llamada CONFIRMADA como terminada, ANTES de limpiarla: si la sesión SIP local sigue activa, se fuerza el cierre.
export function reconcileEndedCall(phone, ended) {
  if (shouldForceLocalHangup({ inCall: phone?.inCall, callState: ended?.state })) {
    phone.forceLocalEnd?.();
    return true;
  }
  return false;
}

// H26-quater (Ticket 4c-quater, maqueta telephony-lab/e2e/mockups/franja_dia.html?v=4 aprobada): la franja
// TU DÍA + CAMPAÑA es la MISMA para asistente y bilingüe (solo lectura en ambos casos; no hay controles en la
// franja) -- PURAS, para no montar DOM (este proyecto no tiene @testing-library/react). `metrics` es la forma
// de GET /campaigns/{id}/metrics (campaign_metrics en el backend): {total_numeros, particion:{pendientes,
// en_curso, por_revisar, terminados:{contestada,rechazada,buzon_confirmado,caso_dudoso,sin_respuesta}},
// vistas:{marcados,...}}. El bilingüe recibe el mismo `particion`/`vistas` dentro de GET /observer/summary
// (`cola.particion`/`cola.vistas`, ver H26-quater en dialer_center.py): NUNCA un conteo aparte.
export function franjaCounts(metrics) {
  const part = metrics?.particion || {};
  const term = part.terminados || {};
  return {
    ok: term.contestada || 0, bz: term.buzon_confirmado || 0, en: part.en_curso || 0,
    re: term.rechazada || 0, du: term.caso_dudoso || 0, sr: term.sin_respuesta || 0,
    pr: part.por_revisar || 0, pe: part.pendientes || 0,
  };
}

// % del anillo de avance: SIEMPRE marcados/total (misma cuenta que ya usa el asistente en la tarjeta central
// y el bilingüe en su progreso, H26-bis: `vistas.marcados`, nunca `status != pendiente`).
export function franjaPct(metrics) {
  const total = metrics?.total_numeros || 0;
  const marcados = metrics?.vistas?.marcados || 0;
  return total ? Math.round((marcados / total) * 100) : 0;
}

// H26-quinquies (Ticket 4c-quinquies, maqueta telephony-lab/e2e/mockups/paneles_simples.html aprobada: "SÍ ASÍ
// MUCHO MÁS ORGANIZADO"): la franja simplificada solo muestra 4 contadores grandes de una vez (los otros 4
// quedan detrás de "Ver más"). PURAS para poder probarlas sin montar DOM.
export function franjaBigStats(metrics) {
  const c = franjaCounts(metrics);
  return [
    { key: "ok", label: "Contestadas", color: "var(--cc-ok)", value: c.ok },
    { key: "bz", label: "Buzón", color: "var(--cc-call)", value: c.bz },
    { key: "en", label: "En curso", color: "var(--cc-ember)", value: c.en },
    { key: "pe", label: "Pendientes", color: "#8d8e99", value: c.pe },
  ];
}
export function franjaMinorStats(metrics) {
  const c = franjaCounts(metrics);
  return [
    { key: "re", label: "Rechazadas", color: "var(--cc-red)", value: c.re },
    { key: "du", label: "Casos dudosos", color: "var(--cc-amber)", value: c.du },
    { key: "sr", label: "Sin respuesta", color: "var(--cc-amber)", value: c.sr },
    { key: "pr", label: "Por revisar", color: "#b58cff", value: c.pr },
  ];
}

// `cola` es la forma que manda GET /observer/summary (campo "total", no "total_numeros"; H26-quater añadió
// "particion"/"vistas" ahí mismo). Esto solo renombra/envuelve para que <FranjaDia> reciba la misma forma de
// "metrics" que ya usa el asistente (GET /campaigns/{id}/metrics) sin dos lecturas distintas de los mismos números.
export function metricsFromCola(cola) {
  if (!cola) return null;
  return { total_numeros: cola.total || 0, vistas: cola.vistas || { marcados: cola.marked || 0 }, particion: cola.particion || {} };
}

// H24-ter (Ticket 4b-3, maqueta telephony-lab/e2e/mockups/tarjeta_campana_simple.html, "PROPUESTA C · SOLO
// ÍCONOS" aprobada): las 3 líneas de texto bajo el selector de campaña ("nombre" repetido, "Ruta: X · Puesto:
// Y", "📋 pegada · cargada ... · es la que marca ahora") se reemplazan por una fila de chips cortos. PURAS.

// Quita el prefijo "Ruta " (si lo trae) y recorta a `max` caracteres con elipsis -- el nombre completo y el
// puesto quedan en el `title` (tooltip), no se pierden, solo no ocupan una línea entera.
export function shortRouteName(routeName, max = 14) {
  const s = String(routeName || "").replace(/^ruta\s+/i, "").trim();
  if (!s) return "";
  return s.length > max ? `${s.slice(0, Math.max(0, max - 1))}…` : s;
}

// "21 oct" (sin hora: la hora completa queda en el title del chip).
export function shortLoadDate(iso) {
  const dt = parseUtc(iso);
  if (!dt) return "";
  return dt.toLocaleDateString("es-DO", { timeZone: "America/Santo_Domingo", day: "numeric", month: "short" });
}

// Estado -> chip pequeño (luz + texto, nunca solo color): verde "Marca ahora" (en_marcha), ámbar "En pausa"
// (pausada), gris "Detenida" (detenida Y lista_para_iniciar -- el chip es un resumen, el detalle fino de
// "lista para iniciar" ya lo dice la franja grande de abajo, aquí no se repite dos veces el mismo matiz).
export const CAMP_CHIP_STATUS = {
  en_marcha: { label: "Marca ahora", color: "ok" },
  pausada: { label: "En pausa", color: "amber" },
  detenida: { label: "Detenida", color: "gray" },
  lista_para_iniciar: { label: "Detenida", color: "gray" },
};
export function campaignChipStatus(status) {
  return CAMP_CHIP_STATUS[status] || { label: "Detenida", color: "gray" };
}
